import { applicantStatusTypes, applicationStatusTypes, uiApplicationStatusTypes } from '../constants/statusConstants';
import {
  getApplicantEmail,
  getApplicantFirstName,
  getApplicantName,
} from '../pages/ApplicationSummary/ApplicationRollup/ApplicationRollup.utils';

function applicationIsDisplayedAsApplicantInvited(application) {
  const { applicants } = application;
  if (applicants.length !== 1) {
    return false;
  }
  const applicant = applicants[0];
  const status = applicant.status;
  if (
    (status !== applicantStatusTypes.INVITED.id && status !== applicantStatusTypes.PENDING.id) ||
    applicant.invitationDate === 0
  ) {
    return false;
  }
  return true;
}

function aggregateSubmittedApplicants(applicants) {
  let mostRecentSubmitDate = 0;
  let submittedApplicantCount = 0;
  for (let i = 0; i < applicants.length; i++) {
    const applicant = applicants[i];
    const { status, submitDate } = applicant;
    if (status === applicantStatusTypes.COMPLETED.id) {
      submittedApplicantCount++;
      if (submitDate > mostRecentSubmitDate) {
        mostRecentSubmitDate = submitDate;
      }
    }
  }

  return {
    mostRecentSubmitDate,
    submittedApplicantCount,
  };
}

function normalizeApplication(application) {
  const {
    applicationId,
    statusType,
    applicants,
    withdrawDate,
    declinedDate,
    acceptedDate,
    invitedToMoveInDate,
    conditionallyApprovedDate,
  } = application;

  if (statusType === applicationStatusTypes.WITHDRAWN.id) {
    return {
      id: applicationId,
      type: uiApplicationStatusTypes.WITHDRAWN.id,
      timestamp: withdrawDate,
    };
  } else if (statusType === applicationStatusTypes.DECLINED.id) {
    return {
      id: applicationId,
      type: applicationStatusTypes.DECLINED.id,
      timestamp: declinedDate,
    };
  } else if (statusType === applicationStatusTypes.ACCEPTED.id) {
    return {
      id: applicationId,
      type: applicationStatusTypes.ACCEPTED.id,
      timestamp: acceptedDate,
    };
  } else if (statusType === applicationStatusTypes.INVITED_TO_MOVE_IN.id) {
    return {
      id: applicationId,
      type: applicationStatusTypes.INVITED_TO_MOVE_IN.id,
      timestamp: invitedToMoveInDate,
    };
  } else if (statusType === applicationStatusTypes.CONDITIONALLY_APPROVED.id) {
    return {
      id: applicationId,
      type: applicationStatusTypes.CONDITIONALLY_APPROVED.id,
      timestamp: conditionallyApprovedDate,
    };
  } else if (applicationIsDisplayedAsApplicantInvited(application)) {
    return {
      id: applicationId,
      type: uiApplicationStatusTypes.INVITED.id,
      timestamp: applicants[0].invitationDate,
    };
  } else {
    let statusType = uiApplicationStatusTypes.HIDDEN.id;

    const aggregatedApplicantInfo = aggregateSubmittedApplicants(applicants);
    const { mostRecentSubmitDate, submittedApplicantCount } = aggregatedApplicantInfo;
    let updateDate = mostRecentSubmitDate;

    if (submittedApplicantCount === applicants.length) {
      statusType = uiApplicationStatusTypes.COMPLETED.id;
    } else if (submittedApplicantCount > 0) {
      statusType = uiApplicationStatusTypes.IN_PROGRESS.id;
    }

    return {
      id: applicationId,
      type: statusType,
      timestamp: updateDate,
    };
  }
}

function getSortedApplications(array) {
  return [...array].sort((a, b) => b.timestamp - a.timestamp).map((application) => application.id);
}

function categorizeApplications(applicationList) {
  const applicationMap = {};
  const { COMPLETED, IN_PROGRESS, WITHDRAWN, DECLINED, INVITED, HIDDEN } = uiApplicationStatusTypes;
  const { ACCEPTED, INVITED_TO_MOVE_IN, CONDITIONALLY_APPROVED } = applicationStatusTypes;
  let categorizedApplicationMap = {
    [COMPLETED.id]: [],
    [INVITED.id]: [],
    [IN_PROGRESS.id]: [],
    [WITHDRAWN.id]: [],
    [DECLINED.id]: [],
    [HIDDEN.id]: [],
    [ACCEPTED.id]: [],
    [INVITED_TO_MOVE_IN.id]: [],
    [CONDITIONALLY_APPROVED.id]: [],
  };

  const len = applicationList.length;
  for (let i = 0; i < len; i++) {
    const application = applicationList[i];
    applicationMap[application.applicationId] = application;

    const normalizedApplication = normalizeApplication(application);
    categorizedApplicationMap[normalizedApplication.type].push(normalizedApplication);
  }

  /**
   * Listing card will not show unless it is flagged as completed, which is
   * the same as received.
   */
  categorizedApplicationMap[COMPLETED.id] = getSortedApplications(categorizedApplicationMap[INVITED_TO_MOVE_IN.id])
    .concat(getSortedApplications(categorizedApplicationMap[CONDITIONALLY_APPROVED.id]))
    .concat(getSortedApplications(categorizedApplicationMap[ACCEPTED.id]))
    .concat(getSortedApplications(categorizedApplicationMap[COMPLETED.id]));
  categorizedApplicationMap[INVITED.id] = getSortedApplications(categorizedApplicationMap[INVITED.id]);
  categorizedApplicationMap[IN_PROGRESS.id] = getSortedApplications(categorizedApplicationMap[IN_PROGRESS.id]);
  categorizedApplicationMap[WITHDRAWN.id] = getSortedApplications(categorizedApplicationMap[WITHDRAWN.id]);
  categorizedApplicationMap[DECLINED.id] = getSortedApplications(categorizedApplicationMap[DECLINED.id]);
  categorizedApplicationMap[ACCEPTED.id] = [];
  categorizedApplicationMap[INVITED_TO_MOVE_IN.id] = [];
  categorizedApplicationMap[CONDITIONALLY_APPROVED.id] = [];

  return {
    applicationMap,
    categorizedApplicationMap,
  };
}

function convertInvitationFromGraphqlToApplicant(invitation) {
  return {
    status: applicantStatusTypes.INVITED.id,
    invitationDate: invitation.invitedDateTime ? new Date(invitation.invitedDateTime).getTime() : 0,
    submitDate: 0,
    firstName: invitation.firstName,
    name: [invitation.firstName, invitation.lastName].filter(Boolean).join(' '),
    email: invitation.email,
  };
}

function calculateStatusTypeFromOverallStatus(overallStatus) {
  switch (overallStatus) {
    case 'withdrawn':
      return applicationStatusTypes.WITHDRAWN.id;
    case 'approved':
      return applicationStatusTypes.INVITED_TO_MOVE_IN.id;
    case 'declined':
      return applicationStatusTypes.DECLINED.id;
    case 'conditionally_approved':
      return applicationStatusTypes.CONDITIONALLY_APPROVED.id;
    case 'invited':
    case 'started':
    case 'invited_and_started':
    case 'partially_submitted':
    case 'submitted':
    default:
      return applicationStatusTypes.ACTIVE.id;
  }
}

function getInvitationDate(applicant) {
  if (applicant?.landlordInvitation?.invitedDateTime) {
    return new Date(applicant.landlordInvitation.invitedDateTime).getTime();
  }
  if (applicant?.coapplicantInvitation?.invitedDateTime) {
    return new Date(applicant.coapplicantInvitation.invitedDateTime).getTime();
  }
  return 0;
}

// Converts response from graphql to expected response from listing applications overview call
export function calculateApplications(data, { includeInvitationsToApply = true } = {}) {
  if (!data) {
    return [];
  }
  const invitationsToApply = includeInvitationsToApply ? data.rentalListing.invitationsToApply : [];
  return [
    ...data.rentalListing.landlordRentalApplications.map((application) => ({
      applicationId: application.id,
      statusType: calculateStatusTypeFromOverallStatus(application.overallStatus),
      applicationIsViewed: application.isViewed,
      applicants: [
        ...application.applicants.map((applicant) => ({
          status: applicant.status,
          // old behaviour defaults these dates to 0 if not present
          // invitationDate did not differntiate between a landlord invitation vs coapplicant
          submitDate: applicant.submittedDateTime ? new Date(applicant.submittedDateTime).getTime() : 0,
          invitationDate: getInvitationDate(applicant),
          firstName: getApplicantFirstName(applicant),
          name: getApplicantName(applicant),
          email: getApplicantEmail(applicant),
        })),
        ...application.invitations.map(convertInvitationFromGraphqlToApplicant),
      ],
      // old behaviour only has these fields if they are actually present
      ...(application.withdrawnDateTime ? { withdrawDate: new Date(application.withdrawnDateTime).getTime() } : {}),
      ...(application.declinedDateTime ? { declinedDate: new Date(application.declinedDateTime).getTime() } : {}),
      ...(application.mergedDateTime ? { mergedDate: new Date(application.mergedDateTime).getTime() } : {}),
      ...(application.conditionallyApprovedDateTime
        ? { conditionallyApprovedDate: new Date(application.conditionallyApprovedDateTime).getTime() }
        : {}),
      ...(application.invitedToMoveInDateTime
        ? {
            invitedToMoveInDate: new Date(application.invitedToMoveInDateTime).getTime(),
            acceptedDate: new Date(application.invitedToMoveInDateTime).getTime(),
          }
        : {}),
    })),
    ...invitationsToApply.map((invitation) => ({
      applicationId: invitation.id,
      statusType: applicationStatusTypes.ACTIVE.id,
      applicationIsViewed: false,
      applicants: [convertInvitationFromGraphqlToApplicant(invitation)],
    })),
  ];
}

export const getApplicantText = ({ applicants }) => {
  if (applicants.length === 1) {
    return 'Single applicant';
  }
  return `${applicants.length} people applying together`;
};

export {
  aggregateSubmittedApplicants,
  applicationIsDisplayedAsApplicantInvited,
  categorizeApplications,
  normalizeApplication,
};
